import { Button } from 'antd';
import { HandCoins, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { PaymentPromisesList } from '../components/PaymentPromisesList';
import { usePaymentPromises } from '../hooks/use-payment-promises';
import type { PaymentPromiseView } from '../payment-promises.api';
import { RecordPromiseModal } from './RecordPromiseModal';

interface PaymentPromisesTabProps {
  customerId: string;
  /** `collection.record` shows Record and Replace; the server stays the authority, so this is UX only. */
  canRecord: boolean;
}

/** What this customer has promised to pay, and whether they kept it. Broken-promise history is the same list filtered to Broken. */
export function PaymentPromisesTab({ customerId, canRecord }: PaymentPromisesTabProps) {
  const promises = usePaymentPromises(customerId);
  const [isOpen, setIsOpen] = useState(false);
  const [replacing, setReplacing] = useState<PaymentPromiseView | null>(null);

  if (promises.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (promises.state.status === 'loading') return <TableSkeleton label="Loading promises to pay" withSearch />;
  if (promises.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the promises" description="Something went wrong loading them. Try reloading the page." />;
  }

  const { list } = promises;
  const isBrokenOnly = list.query.status?.length === 1 && list.query.status[0] === 'broken';

  function open(promise: PaymentPromiseView | null) {
    setReplacing(promise);
    setIsOpen(true);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-muted">
          A promise is kept or broken from the payments received by its date, checked about once an hour, so it can take a little while to show.
        </p>
        <div className="flex gap-2">
          <Button
            icon={<HandCoins className="size-4" />}
            type={isBrokenOnly ? 'primary' : 'default'}
            onClick={() => list.setQuery({ status: isBrokenOnly ? undefined : ['broken'] })}
          >
            Broken-promise history
          </Button>
          {canRecord && (
            <Button type="primary" icon={<Plus className="size-4" />} onClick={() => open(null)}>
              Record promise
            </Button>
          )}
        </div>
      </div>
      <PaymentPromisesList
        items={promises.state.page.items}
        total={promises.state.page.total}
        list={list}
        isRefreshing={promises.isRefreshing}
        onSupersede={canRecord ? open : undefined}
      />
      <RecordPromiseModal
        open={isOpen}
        customerId={customerId}
        supersedesPromiseId={replacing?.id}
        onSaved={() => void promises.reload()}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
}
